// LivePositionsTable.js - Updated with search/filter and complete sorting
import React, { useState, useCallback, useMemo } from "react";
import { ChevronsUpDown, Search, X } from "lucide-react";
import { useGlobalData } from "../../hooks/useGlobalHooks";
import server from "../../utils/serverConfig";

const DriftModal = ({
  isLoading,
  currentPositionData,
  handleCloseDriftModal,
  handlePositionDrift,
}) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-w-md w-full mx-4 animate-in fade-in duration-200">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-zinc-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-violet-600 rounded-lg flex items-center justify-center">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Drift Position</h3>
            <p className="text-sm text-zinc-400">Execute drift strategy</p>
          </div>
        </div>
        <button
          onClick={handleCloseDriftModal}
          className="text-zinc-400 hover:text-white transition-colors p-1"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-zinc-400">Symbol:</span>
              <p className="text-white font-medium">
                {currentPositionData?.symbol?.replace("NSE:", "")}
              </p>
            </div>
            <div>
              <span className="text-zinc-400">Strategy:</span>
              <p className="text-white font-medium">
                {currentPositionData?.strategy}
              </p>
            </div>
            <div>
              <span className="text-zinc-400">Side:</span>
              <p
                className={`font-medium ${
                  currentPositionData?.side === 1
                    ? "text-green-400"
                    : "text-red-400"
                }`}
              >
                {currentPositionData?.side === 1 ? "BUY" : "SELL"}
              </p>
            </div>
            <div>
              <span className="text-zinc-400">Net Qty:</span>
              <p className="text-white font-medium">
                {currentPositionData?.net_qty}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-amber-900/20 border border-amber-600/30 rounded-lg p-4">
          <p className="text-amber-200 text-sm">
            ⚠️ This action will execute the drift strategy for this position.
            Please confirm to proceed.
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 p-6 border-t border-zinc-700">
        <button
          onClick={handleCloseDriftModal}
          className="px-4 py-2 text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-500 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          disabled={isLoading}
          onClick={handlePositionDrift}
          className={`disabled:cursor-not-allowed disabled:bg-violet-600/20 px-6 py-2 bg-violet-600 hover:bg-violet-500 text-white font-medium rounded-lg transition-colors shadow-lg`}
        >
          Execute Drift
        </button>
      </div>
    </div>
  </div>
);

const TPModal = ({
  isLoading,
  currentPositionData,
  handleCloseTPModal,
  handlePositionTP,
}) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-w-md w-full mx-4 animate-in fade-in duration-200">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-zinc-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-amber-600 rounded-lg flex items-center justify-center">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Take Profit</h3>
            <p className="text-sm text-zinc-400">
              Execute take profit strategy
            </p>
          </div>
        </div>
        <button
          onClick={handleCloseTPModal}
          className="text-zinc-400 hover:text-white transition-colors p-1"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-zinc-400">Symbol:</span>
              <p className="text-white font-medium">
                {currentPositionData?.symbol?.replace("NSE:", "")}
              </p>
            </div>
            <div>
              <span className="text-zinc-400">Strategy:</span>
              <p className="text-white font-medium">
                {currentPositionData?.strategy}
              </p>
            </div>
            <div>
              <span className="text-zinc-400">Current P&L:</span>
              <p
                className={`font-medium ${
                  parseFloat(currentPositionData?.pl) > 0
                    ? "text-green-400"
                    : parseFloat(currentPositionData?.pl) < 0
                    ? "text-red-400"
                    : "text-white"
                }`}
              >
                ₹{parseFloat(currentPositionData?.pl)?.toFixed(2)}
              </p>
            </div>
            <div>
              <span className="text-zinc-400">LTP:</span>
              <p className="text-white font-medium">
                ₹{currentPositionData?.ltp?.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-green-900/20 border border-green-600/30 rounded-lg p-4">
          <p className="text-green-200 text-sm">
            📈 This will execute the take profit strategy and potentially close
            profitable positions.
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 p-6 border-t border-zinc-700">
        <button
          onClick={handleCloseTPModal}
          className="px-4 py-2 text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-500 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          disabled={isLoading}
          onClick={handlePositionTP}
          className="disabled:cursor-not-allowed disabled:bg-amber-600/20 px-6 py-2 bg-amber-600 hover:bg-amber-500 text-white font-medium rounded-lg transition-colors shadow-lg"
        >
          Execute TP
        </button>
      </div>
    </div>
  </div>
);

const ExitPositionModal = ({
  isLoading,
  currentPositionData,
  handleCloseExitModal,
  handleExitPosition,
}) => (
  <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-w-md w-full mx-4 animate-in fade-in duration-200">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-zinc-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-rose-600 rounded-lg flex items-center justify-center">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Exit Position</h3>
            <p className="text-sm text-zinc-400">Close entire position</p>
          </div>
        </div>
        <button
          onClick={handleCloseExitModal}
          className="text-zinc-400 hover:text-white transition-colors p-1"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="p-6 space-y-4">
        <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-zinc-400">Symbol:</span>
              <p className="text-white font-medium">
                {currentPositionData?.symbol?.replace("NSE:", "")}
              </p>
            </div>
            <div>
              <span className="text-zinc-400">Strategy:</span>
              <p className="text-white font-medium">
                {currentPositionData?.strategy}
              </p>
            </div>
            <div>
              <span className="text-zinc-400">Net Qty:</span>
              <p className="text-white font-medium">
                {currentPositionData?.net_qty}
              </p>
            </div>
            <div>
              <span className="text-zinc-400">Final P&L:</span>
              <p
                className={`font-medium ${
                  parseFloat(currentPositionData?.pl) > 0
                    ? "text-green-400"
                    : parseFloat(currentPositionData?.pl) < 0
                    ? "text-red-400"
                    : "text-white"
                }`}
              >
                ₹{parseFloat(currentPositionData?.pl)?.toFixed(2)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-4">
          <p className="text-red-200 text-sm">
            ⚠️ This action will close the entire position immediately. This
            action cannot be undone.
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 p-6 border-t border-zinc-700">
        <button
          onClick={handleCloseExitModal}
          className="px-4 py-2 text-zinc-300 hover:text-white border border-zinc-600 hover:border-zinc-500 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          disabled={isLoading}
          onClick={handleExitPosition}
          className="disabled:cursor-not-allowed disabled:bg-rose-600/20 px-6 py-2 bg-rose-600 hover:bg-rose-500 text-white font-medium rounded-lg transition-colors shadow-lg"
        >
          Exit Position
        </button>
      </div>
    </div>
  </div>
);

// Target account for specific metrics
const TARGET_CLIENT_ID = "CA00246";

// PositionRow Component
const PositionRow = React.memo(
  ({
    position,
    index,
    connectionStatus,
    botDataFlowStatus,
    onClosePosition,
    handleOpenDriftModal,
    handleOpenTPModal,
    handleOpenExitModal,
  }) => {
    const formatCurrency = useCallback((amount) => {
      if (amount === null || amount === undefined) return "₹0.00";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }, []);

    const formatNumber = useCallback((num) => {
      if (num === null || num === undefined) return "0";
      return new Intl.NumberFormat("en-IN").format(num);
    }, []);

    const getPnLColor = useCallback((pnl) => {
      if (pnl > 0) return "text-green-400";
      if (pnl < 0) return "text-red-400";
      return "text-white";
    }, []);

    const getSideColor = useCallback((side) => {
      return side === 1 ? "text-green-400" : "text-red-400";
    }, []);

    // Use current_ltp (from frontend context) instead of ltp
    const currentLtp = position.current_ltp || position.ltp || 0;
    const avgPrice = parseFloat(position.net_avg) || 0;

    // Calculate price change for display
    const priceChange = currentLtp - avgPrice;
    const priceChangePercent = avgPrice
      ? ((priceChange / avgPrice) * 100).toFixed(2)
      : "0.00";

    // Highlight target account positions
    const isTargetAccount = position.client_id === TARGET_CLIENT_ID;

    const getDataFlowIndicator = useCallback(() => {
      if (
        connectionStatus.includes("Connected") &&
        botDataFlowStatus === "active"
      ) {
        return { color: "bg-green-400", animate: "animate-pulse" };
      } else if (connectionStatus.includes("Connected")) {
        return { color: "bg-yellow-400", animate: "" };
      } else {
        return { color: "bg-red-400", animate: "" };
      }
    }, [connectionStatus, botDataFlowStatus]);

    const dataFlowIndicator = getDataFlowIndicator();
    const lastUpdated = position.last_updated
      ? new Date(position.last_updated).toLocaleTimeString()
      : "N/A";

    return (
      <tr
        className={`h-8 ${
          isTargetAccount ? "ring-1 ring-blue-500/30" : ""
        } hover:bg-zinc-800 transition-all duration-300 border-b border-zinc-700/50 bg-zinc-900`}
      >
        <td className="px-2 py-1 text-left border-r border-zinc-700/30 min-w-0">
          <div className="text-white font-medium text-xs leading-tight truncate">
            {position.symbol}
          </div>
          <div
            className={`text-[10px] leading-none ${
              priceChange >= 0 ? "text-green-400" : "text-red-400"
            }`}
          >
            {priceChange >= 0 ? "+" : ""}
            {formatCurrency(priceChange)} ({priceChangePercent}%)
          </div>
          <div
            className="text-[8px] text-zinc-500 truncate"
            title={`ID: ${position.id}, Updated: ${lastUpdated}`}
          >
            ID: {position.id}
          </div>
        </td>

        <td
          className={`px-2 py-1 text-center font-medium text-xs border-r border-zinc-700/30 ${getSideColor(
            position.side
          )}`}
        >
          {position.side === 1 ? "BUY" : "SELL"}
        </td>

        <td className="px-2 py-1 text-right text-white text-xs border-r border-zinc-700/30">
          {formatNumber(parseFloat(position.net_qty || 0))}
        </td>

        <td className="px-2 py-1 text-right text-white text-xs border-r border-zinc-700/30">
          {formatCurrency(avgPrice)}
        </td>

        <td className="px-2 py-1 text-right border-r border-zinc-700/30">
          <div className="flex items-center justify-end space-x-1">
            <span className="font-medium text-xs text-white">
              {formatCurrency(currentLtp)}
            </span>
            <div
              className={`w-1.5 h-1.5 rounded-full ${dataFlowIndicator.color} ${dataFlowIndicator.animate}`}
              title={`Data Status: ${botDataFlowStatus}`}
            ></div>
          </div>
        </td>

        <td className="px-2 py-1 text-center text-white font-medium text-xs border-r border-zinc-700/30 truncate">
          {position.strategy || "N/A"}
        </td>

        <td
          className={`px-2 py-1 text-right font-medium text-xs border-r border-zinc-700/30 ${getPnLColor(
            parseFloat(position.pl) || 0
          )}`}
        >
          {formatCurrency(parseFloat(position.pl) || 0)}
        </td>

        <td
          className={`px-2 py-1 text-center text-xs border-r border-zinc-700/30 truncate ${
            isTargetAccount ? "text-blue-400 font-medium" : "text-zinc-400"
          }`}
        >
          {position.client_id || "N/A"}
        </td>

        <td className="px-2 py-1 text-center">
          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={() => handleOpenDriftModal(position)}
              className="flex items-center justify-center bg-violet-800 hover:bg-violet-500 text-white font-medium rounded px-1 py-0.5 text-[10px] transition-colors"
              title="Modify position"
            >
              Drift
            </button>
            <button
              onClick={() => handleOpenTPModal(position)}
              className="flex items-center justify-center bg-amber-800 hover:bg-amber-500 text-white font-medium rounded px-1 py-0.5 text-[10px] transition-colors"
              title="Take profit"
            >
              TP
            </button>
            <button
              onClick={() => handleOpenExitModal(position)}
              className="flex items-center justify-center bg-rose-800 hover:bg-rose-500 text-white font-medium rounded px-1 py-0.5 text-[10px] transition-colors"
              title={`Close position: ${position.symbol}`}
            >
              Exit
            </button>
          </div>
        </td>
      </tr>
    );
  }
);

export default function LivePositionsTable() {
  const globalData = useGlobalData();
  const {
    positions = [],
    wsConnected,
    reconnectWebSocket,
    pingWebSocket,
    refreshAll,
    currentLTPs = {},
  } = globalData;

  // Compute derived values
  const connectionStatus = wsConnected ? "Connected" : "Disconnected";
  const totalPnL = useMemo(() => {
    return positions?.reduce((sum, pos) => sum + (pos.pl || 0), 0) || 0;
  }, [positions]);
  const reconnect =
    reconnectWebSocket || (() => console.log("No reconnect function"));
  const sendPing = pingWebSocket || (() => console.log("No sendPing function"));
  const requestRefresh =
    refreshAll || (() => console.log("No requestRefresh function"));
  const closePosition = () => console.log("closePosition not yet implemented");
  const isConnecting = false;
  const botStatus = "disconnected";
  const botConnections = 0;
  const botDataFlowStatus = "no_data";
  const ltpUpdateCount = Object.keys(currentLTPs).length;
  const lastLtpTime = Date.now();
  const lastUpdateTime = Date.now();

  const [showDriftModal, setShowDriftModal] = useState(false);
  const [showTPModal, setShowTPModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [currentPositionData, setCurrentPositionData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Local state for search/filter and sorting
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "symbol",
    direction: "asc",
  });

  // Clear search function
  const clearSearch = useCallback(() => {
    setSearchTerm("");
  }, []);

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  // Filter positions based on search term
  const filteredPositions = useMemo(() => {
    if (!searchTerm.trim()) {
      return positions;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return positions.filter((position) => {
      const symbol = (position.symbol || "").toLowerCase();
      const clientId = (position.client_id || "").toLowerCase();
      const strategy = (position.strategy || "").toLowerCase();

      return (
        symbol.includes(searchLower) ||
        clientId.includes(searchLower) ||
        strategy.includes(searchLower)
      );
    });
  }, [positions, searchTerm]);

  // Account metrics based on filtered positions
  const accountMetrics = useMemo(() => {
    console.log(
      "🔍 ALL POSITIONS:",
      positions?.map((p) => ({
        symbol: p.symbol,
        client_id: p.client_id,
        id: p.id,
        pl: p.pl,
      }))
    );

    const positionIds = positions?.map((p) => p.id);
    const uniqueIds = new Set(positionIds);
    if (positionIds.length !== uniqueIds.size) {
      console.warn("🔍 DUPLICATE POSITION IDS DETECTED!");
    }

    const allClientIds = [...new Set(positions?.map((p) => p.client_id))];
    console.log("🔍 ALL CLIENT IDS FOUND:", allClientIds);

    const accountPositions = positions.filter((pos) => {
      const hasValidClientId =
        pos.client_id && pos.client_id === TARGET_CLIENT_ID;
      return hasValidClientId;
    });

    console.log(
      `🔍 FILTERED POSITIONS FOR ${TARGET_CLIENT_ID}:`,
      accountPositions.length
    );

    const totalPositions = accountPositions.length;
    const uniqueSymbols = new Set(accountPositions.map((pos) => pos.symbol))
      .size;
    const totalAccountPnL = accountPositions.reduce((sum, pos) => {
      const pnl = parseFloat(pos.pl) || 0;
      return sum + pnl;
    }, 0);

    return {
      totalPositions,
      uniqueSymbols,
      totalAccountPnL,
    };
  }, [positions]);

  // Sorting function
  const handleSort = useCallback((key) => {
    setSortConfig((prevSort) => ({
      key,
      direction:
        prevSort.key === key && prevSort.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  // Sorted and filtered positions
  const sortedPositions = useMemo(() => {
    const validPositions = filteredPositions.filter((position) => {
      return position && position.id && position.symbol;
    });

    const sorted = [...validPositions].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle numeric fields
      if (
        ["net_qty", "net_avg", "ltp", "pl", "side"].includes(sortConfig.key)
      ) {
        if (sortConfig.key === "ltp") {
          aValue = a.current_ltp || a.ltp || 0;
          bValue = b.current_ltp || b.ltp || 0;
        } else if (sortConfig.key === "side") {
          aValue = a.side || 0;
          bValue = b.side || 0;
        } else {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }

        if (sortConfig.key === "net_qty") {
          aValue = parseInt(aValue);
          bValue = parseInt(bValue);
        }
      }

      // Handle string fields
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
      }
      if (typeof bValue === "string") {
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [filteredPositions, sortConfig]);

  // Utility functions
  const formatCurrency = useMemo(
    () => (amount) => {
      if (amount === null || amount === undefined) return "₹0.00";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    },
    []
  );

  const getPnLColor = useMemo(
    () => (pnl) => {
      if (pnl > 0) return "text-green-400";
      if (pnl < 0) return "text-red-400";
      return "text-white";
    },
    []
  );

  const getBotStatusIndicator = useCallback(() => {
    console.log("connectionStatus is ", connectionStatus);
    if (connectionStatus.includes("Connected")) {
      if (botDataFlowStatus === "active") {
        return {
          color: "bg-green-500",
          animate: "animate-pulse",
          text: "ACTIVE",
        };
      } else {
        return { color: "bg-yellow-500", animate: "", text: "IDLE" };
      }
    } else {
      return { color: "bg-red-500", animate: "", text: "OFFLINE" };
    }
  }, [connectionStatus, botDataFlowStatus]);

  const botStatusIndicator = getBotStatusIndicator();

  const ltpUpdateRate = useMemo(() => {
    const now = Date.now();
    const timeSinceLastLtp = now - lastLtpTime;

    if (timeSinceLastLtp < 10000) {
      return `${Math.round(timeSinceLastLtp / 1000)}s ago`;
    } else if (timeSinceLastLtp < 60000) {
      return `${Math.round(timeSinceLastLtp / 1000)}s ago`;
    } else if (timeSinceLastLtp < 3600000) {
      return `${Math.round(timeSinceLastLtp / 60000)}m ago`;
    } else {
      return "Stale";
    }
  }, [lastLtpTime, lastUpdateTime]);

  // Show connecting state
  if (positions.length === 0 && connectionStatus.includes("Connecting")) {
    return (
      <div className="w-full h-[calc(100vh-100px)] bg-zinc-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-white text-lg mb-2">
            Connecting to Position Stream...
          </div>
          <div className="text-zinc-400 text-sm">
            Status: {connectionStatus}
          </div>
        </div>
      </div>
    );
  }

  // Modal handlers
  const handleCloseDriftModal = () => setShowDriftModal(false);
  const handleCloseTPModal = () => setShowTPModal(false);
  const handleCloseExitModal = () => setShowExitModal(false);

  const handleOpenDriftModal = (position) => {
    setCurrentPositionData(position);
    setShowDriftModal(true);
  };

  const handleOpenTPModal = (position) => {
    setCurrentPositionData(position);
    setShowTPModal(true);
  };

  const handleOpenExitModal = (position) => {
    setCurrentPositionData(position);
    setShowExitModal(true);
  };

  const handlePositionDrift = () => {
    setIsLoading(true);
    if (currentPositionData !== null) {
      //  console.log("Drift Position", currentPositionData);

      const payload = {
        symbol: currentPositionData.symbol,
        side: currentPositionData.side,
        netAvg: currentPositionData.netAvg,
        ltp: currentPositionData.ltp,
        strategy: currentPositionData.strategy,
        pl: currentPositionData.pl,
        clientIds: {
          [currentPositionData.clientIds[0]]: currentPositionData.net_qty,
        },
      };

      console.log("Restructured Payload:", payload);

      let config = {
        method: "post",
        url: `${server.godseye.baseUrl}drift_position`,
        headers: {
          "Content-Type": "application/json",
        },
        data: JSON.stringify(payload),
      };

      //  axios
      //    .request(config)
      //    .then((response) => {
      //      console.log("Drift Position response:", response.data);

      //      if (response.data.status === 200) {
      //        toast.success("Position Drifted Successful !!", {
      //          duration: 5000,
      //        });
      //      } else {
      //        toast.error("Position Drift Failed !!", {
      //          duration: 5000,
      //        });
      //      }
      //      handleCloseDriftModal();
      //      setIsLoading(false);
      //    })
      //    .catch((error) => {
      //      console.log("Error in position request:", error);
      //      handleCloseDriftModal();
      //      setIsLoading(false);
      //    });
    } else {
      alert("Please select a position for Drift");
      setIsLoading(false);
    }
  };

  const handlePositionTP = () => {
    setIsLoading(true);
    if (currentPositionData !== null) {
      //  console.log("TP Position", currentPositionData);

      const payload = {
        symbol: currentPositionData.symbol,
        side: currentPositionData.side,
        netAvg: currentPositionData.netAvg,
        ltp: currentPositionData.ltp,
        strategy: currentPositionData.strategy,
        pl: currentPositionData.pl,
        clientIds: {
          [currentPositionData.clientIds[0]]: currentPositionData.net_qty,
        },
      };

      console.log("Restructured Payload:", payload);

      let config = {
        method: "post",
        url: `${server.godseye.baseUrl}tp_position`,
        headers: {
          "Content-Type": "application/json",
        },
        data: JSON.stringify(payload),
      };

      //  axios
      //    .request(config)
      //    .then((response) => {
      //      console.log("TP Position response:", response.data);

      //      if (response.data.status === 200) {
      //        toast.success("Position TP Successful !!", {
      //          duration: 5000,
      //        });
      //      } else {
      //        toast.error("Position TP Failed !!", {
      //          duration: 5000,
      //        });
      //      }
      //      handleCloseTPModal();
      //       setIsLoading(false);
      //    })
      //    .catch((error) => {
      //      console.log("Error in position request:", error);
      //      handleCloseTPModal();
      //       setIsLoading(false);
      //    });
    } else {
      alert("Please select a position for TP");
      setIsLoading(false);
    }
  };

  const handleExitPosition = () => {
    setIsLoading(true);
    if (currentPositionData !== null) {
      //  console.log("EXIT Position", currentPositionData);

      const payload = {
        symbol: currentPositionData.symbol,
        side: currentPositionData.side,
        netAvg: currentPositionData.netAvg,
        ltp: currentPositionData.ltp,
        strategy: currentPositionData.strategy,
        pl: currentPositionData.pl,
        clientIds: {
          [currentPositionData.clientIds[0]]: currentPositionData.net_qty,
        },
      };

      console.log("Restructured Payload:", payload);

      let config = {
        method: "post",
        url: `${server.godseye.baseUrl}exit_position`,
        headers: {
          "Content-Type": "application/json",
        },
        data: JSON.stringify(payload),
      };

      //  axios
      //    .request(config)
      //    .then((response) => {
      //      console.log("Exit Position response:", response.data);

      //      if (response.data.status === 200) {
      //        toast.success("Position Exit Successful !!", {
      //          duration: 5000,
      //        });
      //      } else {
      //        toast.error("Position Exit Failed !!", {
      //          duration: 5000,
      //        });
      //      }
      //      handleCloseExitModal();
      //      setIsLoading(false);
      //    })
      //    .catch((error) => {
      //      console.log("Error in exit position request:", error);
      //      handleCloseExitModal();
      //      setIsLoading(false);
      //    });
    } else {
      alert("Please select a position for exit");
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full h-[calc(100vh-100px)] bg-zinc-900 flex flex-col">
      {/* Header with search and metrics */}
      <div className="w-full bg-zinc-900 border-b border-zinc-800 px-6 py-3 shrink-0">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center space-x-6">
            {/* Position count */}
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-zinc-300">All:</span>
              <span className="text-lg font-semibold text-white">
                {sortedPositions.length} positions
              </span>
              {searchTerm && positions.length !== sortedPositions.length && (
                <span className="text-xs text-zinc-400">
                  (filtered from {positions.length})
                </span>
              )}
            </div>

            {/* Total P&L */}
            <div className="flex items-center space-x-2">
              <span className="text-lg font-semibold text-white">
                Total P&L:
              </span>
              <span className={`text-lg font-bold ${getPnLColor(totalPnL)}`}>
                {formatCurrency(totalPnL)}
              </span>
            </div>

            {/* Account-Specific Metrics */}
            <div className="border-l border-zinc-600 pl-6">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-blue-300">
                    {TARGET_CLIENT_ID}:
                  </span>
                  <span className="text-base font-semibold text-blue-400">
                    {accountMetrics.totalPositions} total
                  </span>
                  <span className="text-sm text-blue-300">|</span>
                  <span className="text-base font-semibold text-blue-400">
                    {accountMetrics.uniqueSymbols} unique
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-base font-semibold text-blue-300">
                    P&L:
                  </span>
                  <span
                    className={`text-base font-bold ${getPnLColor(
                      accountMetrics.totalAccountPnL
                    )}`}
                  >
                    {formatCurrency(accountMetrics.totalAccountPnL)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Bot status display */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div
                  className={`w-2 h-2 rounded-full ${botStatusIndicator.color} ${botStatusIndicator.animate}`}
                ></div>
                <span className="text-sm text-white">
                  Bot: {botStatusIndicator.text}
                </span>
              </div>
            </div>

            {/* Control buttons */}
            <div className="flex space-x-2">
              <button
                onClick={sendPing}
                className="px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 transition-colors disabled:opacity-50"
                disabled={!connectionStatus.includes("Connected")}
                title="Test connection with ping"
              >
                Ping
              </button>
              <button
                onClick={requestRefresh}
                className="px-2 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 transition-colors disabled:opacity-50"
                disabled={!connectionStatus.includes("Connected")}
                title="Request position refresh from backend"
              >
                Refresh
              </button>
              <button
                onClick={reconnect}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                disabled={isConnecting}
              >
                {isConnecting ? "Connecting..." : "Reconnect"}
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="flex items-center space-x-4">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-zinc-400" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by Symbol, Client ID, or Strategy..."
              className="block w-full pl-10 pr-8 py-2 border border-zinc-600 rounded-md bg-zinc-800 text-white placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-white text-zinc-400 transition-colors"
                title="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {searchTerm && (
            <div className="text-sm text-zinc-400">
              {sortedPositions.length} result
              {sortedPositions.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {sortedPositions.length === 0 ? (
        <div className="w-full flex-1 bg-zinc-900 flex flex-col items-center justify-center text-zinc-400">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <div
                className={`w-4 h-4 rounded-full mr-2 ${botStatusIndicator.color} ${botStatusIndicator.animate}`}
              ></div>
              <div className="text-lg font-medium">
                {searchTerm
                  ? "No Matching Positions"
                  : connectionStatus.includes("Connected")
                  ? "No Positions Found"
                  : "Backend Disconnected"}
              </div>
            </div>
            <div className="text-sm text-zinc-500">
              {searchTerm
                ? `No positions match "${searchTerm}"`
                : connectionStatus.includes("Connected")
                ? "Waiting for position data from trading system..."
                : "Backend connection lost. Reconnecting..."}
            </div>
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
              >
                Clear Search
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          {/* Table Header */}
          <div className="bg-zinc-800 border-b border-zinc-600 sticky top-0 z-10 shrink-0">
            <table className="w-full border-collapse table-fixed">
              <colgroup>
                <col className="w-32" />
                <col className="w-20" />
                <col className="w-20" />
                <col className="w-24" />
                <col className="w-24" />
                <col className="w-24" />
                <col className="w-24" />
                <col className="w-20" />
                <col className="w-28" />
              </colgroup>
              <thead>
                <tr className="h-8">
                  <th
                    className="h-8 px-2 py-1 text-left text-xs font-semibold text-zinc-300 border-r border-zinc-600/50 cursor-pointer hover:text-white hover:bg-zinc-700 transition-colors"
                    onClick={() => handleSort("symbol")}
                  >
                    <div className="flex items-center space-x-1 h-full">
                      <span>Symbol</span>
                      <ChevronsUpDown
                        className={`w-3 h-3 ${
                          sortConfig.key === "symbol" ? "text-blue-400" : ""
                        }`}
                      />
                    </div>
                  </th>
                  <th
                    className="h-8 px-2 py-1 text-center text-xs font-semibold text-zinc-300 border-r border-zinc-600/50 cursor-pointer hover:text-white hover:bg-zinc-700 transition-colors"
                    onClick={() => handleSort("side")}
                  >
                    <div className="flex items-center justify-center space-x-1 h-full">
                      <span>Side</span>
                      <ChevronsUpDown
                        className={`w-3 h-3 ${
                          sortConfig.key === "side" ? "text-blue-400" : ""
                        }`}
                      />
                    </div>
                  </th>
                  <th
                    className="h-8 px-2 py-1 text-center text-xs font-semibold text-zinc-300 border-r border-zinc-600/50 cursor-pointer hover:text-white hover:bg-zinc-700 transition-colors"
                    onClick={() => handleSort("net_qty")}
                  >
                    <div className="flex items-center justify-center space-x-1 h-full">
                      <span>Qty</span>
                      <ChevronsUpDown
                        className={`w-3 h-3 ${
                          sortConfig.key === "net_qty" ? "text-blue-400" : ""
                        }`}
                      />
                    </div>
                  </th>
                  <th
                    className="h-8 px-2 py-1 text-center text-xs font-semibold text-zinc-300 border-r border-zinc-600/50 cursor-pointer hover:text-white hover:bg-zinc-700 transition-colors"
                    onClick={() => handleSort("net_avg")}
                  >
                    <div className="flex items-center justify-center space-x-1 h-full">
                      <span>Avg Price</span>
                      <ChevronsUpDown
                        className={`w-3 h-3 ${
                          sortConfig.key === "net_avg" ? "text-blue-400" : ""
                        }`}
                      />
                    </div>
                  </th>
                  <th
                    className="h-8 px-2 py-1 text-center text-xs font-semibold text-zinc-300 border-r border-zinc-600/50 cursor-pointer hover:text-white hover:bg-zinc-700 transition-colors"
                    onClick={() => handleSort("ltp")}
                  >
                    <div className="flex items-center justify-center space-x-1 h-full">
                      <span>LTP</span>
                      <ChevronsUpDown
                        className={`w-3 h-3 ${
                          sortConfig.key === "ltp" ? "text-blue-400" : ""
                        }`}
                      />
                    </div>
                  </th>
                  <th
                    className="h-8 px-2 py-1 text-center text-xs font-semibold text-zinc-300 border-r border-zinc-600/50 cursor-pointer hover:text-white hover:bg-zinc-700 transition-colors"
                    onClick={() => handleSort("strategy")}
                  >
                    <div className="flex items-center justify-center space-x-1 h-full">
                      <span>Strategy</span>
                      <ChevronsUpDown
                        className={`w-3 h-3 ${
                          sortConfig.key === "strategy" ? "text-blue-400" : ""
                        }`}
                      />
                    </div>
                  </th>
                  <th
                    className="h-8 px-2 py-1 text-center text-xs font-semibold text-zinc-300 border-r border-zinc-600/50 cursor-pointer hover:text-white hover:bg-zinc-700 transition-colors"
                    onClick={() => handleSort("pl")}
                  >
                    <div className="flex items-center justify-center space-x-1 h-full">
                      <span>P&L</span>
                      <ChevronsUpDown
                        className={`w-3 h-3 ${
                          sortConfig.key === "pl" ? "text-blue-400" : ""
                        }`}
                      />
                    </div>
                  </th>
                  <th
                    className="h-8 px-2 py-1 text-center text-xs font-semibold text-zinc-300 border-r border-zinc-600/50 cursor-pointer hover:text-white hover:bg-zinc-700 transition-colors"
                    onClick={() => handleSort("client_id")}
                  >
                    <div className="flex items-center justify-center space-x-1 h-full">
                      <span>Client</span>
                      <ChevronsUpDown
                        className={`w-3 h-3 ${
                          sortConfig.key === "client_id" ? "text-blue-400" : ""
                        }`}
                      />
                    </div>
                  </th>
                  <th className="h-8 px-2 py-1 text-center text-xs font-semibold text-zinc-300">
                    <div className="flex items-center justify-center h-full">
                      <span>Actions</span>
                    </div>
                  </th>
                </tr>
              </thead>
            </table>
          </div>

          {/* Scrollable Table Body */}
          <div className="flex-1 overflow-y-auto min-h-0">
            <table className="w-full bg-zinc-900 border-collapse table-fixed">
              <colgroup>
                <col className="w-32" />
                <col className="w-20" />
                <col className="w-20" />
                <col className="w-24" />
                <col className="w-24" />
                <col className="w-24" />
                <col className="w-24" />
                <col className="w-20" />
                <col className="w-28" />
              </colgroup>
              <tbody>
                {sortedPositions.map((position, index) => (
                  <PositionRow
                    key={`${position.id}-${position.symbol}`}
                    position={position}
                    index={index}
                    connectionStatus={connectionStatus}
                    botDataFlowStatus={botDataFlowStatus}
                    onClosePosition={closePosition}
                    handleOpenDriftModal={handleOpenDriftModal}
                    handleOpenTPModal={handleOpenTPModal}
                    handleOpenExitModal={handleOpenExitModal}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Modals */}
      {showDriftModal && (
        <DriftModal
          currentPositionData={currentPositionData}
          handleCloseDriftModal={handleCloseDriftModal}
          handlePositionDrift={handlePositionDrift}
          isLoading={isLoading}
        />
      )}

      {showTPModal && (
        <TPModal
          currentPositionData={currentPositionData}
          handleCloseTPModal={handleCloseTPModal}
          handlePositionTP={handlePositionTP}
          isLoading={isLoading}
        />
      )}

      {showExitModal && (
        <ExitPositionModal
          currentPositionData={currentPositionData}
          handleCloseExitModal={handleCloseExitModal}
          handleExitPosition={handleExitPosition}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}
